import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { addMonths, addQuarters, addYears, intervalToDuration } from "date-fns";
import ObjectEdit from "@components/objects/ObjectEdit";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import { ObjectRelationshipEdit } from "@components/objects/ObjectRelationshipEdit";
import { countries, donationFrequencies, donationTypes } from "@utils/selectOptions";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import SearchSelect from "@components/SearchSelect";
import { CheckPermission } from "@components/CheckPermission";
import { useDeleteBusiness, useGetBusiness, useUpdateBusiness } from "@api/businessService";
import { ContextualHelp } from "@components/ContextualHelp";
import { useDeleteProperty, useGetProperty, useUpdateProperty } from "@api/propertyService";
import { useDeleteDonation, useGetDonation, useUpdateDonation } from "@api/donationService";

const donorInfoFields = [
    {
        name: 'donor_type',
        type: 'segmented', // radio
        options: [
            {label: 'Contact', value: 'App\\Models\\Contact'},
            {label: 'Business', value: 'App\\Models\\Business'},
        ],
        // TODO: figure out how to clear the donor ID when swapping the type
    },
    {
        name: 'donor_id',
        type: 'info',
        content: '(Select an donor type)',
        conditional: object => !object?.donor_type,
    },
    {
        name: 'donor_id',
        type: 'relationship',
        endpoint: 'contacts',
        defaultProp: 'donor',
        conditional: object => !!(object?.donor_type?.includes('Contact')),
    },
    {
        name: 'donor_id',
        type: 'relationship',
        endpoint: 'businesses',
        defaultProp: 'donor',
        conditional: object => !!(object?.donor_type?.includes('Business')),
    },
    {
        name: 'anonymous',
        label: '',
        type: 'boolean',
        inputLabel: 'Donor wishes to remain anonymous',
    },
];

const donationInfoFields = [
    {
        name: 'type',
        type: 'select',
        options: donationTypes,
        // endpoint: 'donation-types',
        defaultProp: 'type',
        // loadAll: true,
    },
    {
        name: 'campaign_id',
        type: 'relationship',
        endpoint: 'campaigns',
        defaultProp: 'campaign',
        loadAll: true,
    },
    {
        name: 'amount',
        type: 'number',
        step: 'any',
        startDecorator: '$',
        placeholder: 'e.g. 500',
    },
    {
        name: 'fund_id',
        type: 'relationship',
        endpoint: 'funds',
        defaultProp: 'fund',
        loadAll: true,
    },
    {
        name: 'appeal_id',
        type: 'relationship',
        endpoint: 'appeals',
        defaultProp: 'appeal',
        loadAll: true,
    },
    {
        name: 'invoice_num',
        type: 'text',
        label: 'Invoice Number',
        placeholder: 'e.g. 89237',
    },
    {
        name: 'notes',
        type: 'textarea',
        placeholder: 'Enter notes...',
    },
];

const donationRecurrencesFields = [
    {
        name: 'frequency',
        type: 'select',
        options: donationFrequencies,
    },
    {
        name: 'num_donations',
        label: 'Number of Donations',
        type: 'number',
        // startDecorator: '$',
        placeholder: 'e.g. 6',
        tooltip: 'How many times will this donation happen?',
    },
    {
        name: 'start_date',
        type: 'date',
    },
];

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Avatar size="lg" sx={{'--Avatar-size': '5rem'}} src={data?.profile_image?.url}>{avatarInitials}</Avatar>
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>
            {data.title && <Typography fontSize={'sm'}>{data.title}</Typography>}
            {data.company_name && data.name !== data.company_name && <Typography fontSize={'sm'}>{data.company_name}</Typography>}
        </Box>
    </Stack>
)
};

export default function DonationEdit(props) {
    const beforeSave = (object) => {
        if(!object.frequency) {
            object.frequency = object.type === 'Recurring' ? 'Monthly' : 'Single';
        }

        // create initial donation entries
        if(!object.id && !object.hasOwnProperty('donation_entries')) {
            let entries = [];

            let num_donations = object.num_donations ? object.num_donations : 1;

            while (entries.length < num_donations) {
                let $newDate;
                if (entries.length == 0) {
                    $newDate = object.start_date != null ? ensureDateObject(object.start_date) : new Date();
                } else {
                    $newDate = ensureDateObject(entries[entries.length -1].donation_date);

                    switch(object.frequency) {
                        case "Monthly":
                            $newDate = addMonths($newDate, 1);
                            break;
                        case "Quarterly":
                            $newDate = addQuarters($newDate, 1);
                            break;
                        case "Yearly":
                            $newDate = addYears($newDate, 1);
                            break;
                        // If it doesn't match any of these, just add a month.
                        default:
                            $newDate = addMonths($newDate, 1);
                            break;
                    }
                }

                let $newEntry = {
                    id: null,
                    donation_id: null,
                    amount: object.amount,
                    invoice_num: object.invoice_num,
                    donation_date: $newDate,//.format('YYYY-MM-DD'),
                };

                entries.push($newEntry);
            }

            object.donation_entries = entries;
        }

        // Sync amount to existing entries when editing
        if(object.id && object.donation_entries) {
            object.donation_entries = object.donation_entries.map(entry => ({
                ...entry,
                amount: entry.received ? entry.amount : object.amount,
            }));
        }

        return object;
    };

    return (
        <ObjectEdit
            query={useGetDonation}
            mutation={useUpdateDonation}
            deleteMutation={useDeleteDonation}
            type={'donation'}
            permissionType={'donations'}
            title={'Edit Donation'}
            subtitleKey={'name'}
            color={'default'}
            beforeSave={beforeSave}
            {...props}
        >
            <ObjectColumn primary>
                <ObjectPrimaryInfoEdit
                    title={'Donor Info'}
                    tint={'default'}
                    fields={donorInfoFields}
                />
            </ObjectColumn>

            <ObjectColumn>
                <ObjectPrimaryInfoEdit
                    title={'Donation Info'}
                    tint={'default'}
                    fields={donationInfoFields}
                />
                {/*
                <ObjectRelationshipEdit
                    type={'donation_info'}
                    permissions={'edit donations'}
                />
                */}
            </ObjectColumn>

            <ObjectColumn>
                <ObjectPrimaryInfoEdit
                    title={'Donation Recurrences'}
                    tint={'default'}
                    fields={donationRecurrencesFields}
                    visible={(object) => object?.type === 'Recurring'}
                />
                {/*
                <ObjectRelationshipEdit
                    visible={(object) => !!object?.type === 'Recurring'}
                    type={'donation_recurrences'}
                    permissions={'edit donations'}
                />
                */}
            </ObjectColumn>

        </ObjectEdit>
    )
}
